/**
 * RouterL7RulesTab
 *
 * Manages MikroTik Layer 7 firewall rules for controlling outbound internet services
 * (e.g., WhatsApp, YouTube, Microsoft 365) across all provisioned routers.
 *
 * L7 rules work by:
 *   1. Matching application-layer traffic via a POSIX regex pattern on each router
 *      using MikroTik's /ip firewall layer7-protocol feature.
 *   2. Applying a firewall filter rule that references the L7 protocol to drop or accept
 *      matching traffic on the configured chain (typically "forward" for outbound LAN traffic).
 *
 * To make this accessible to non-technical users, the component provides a visual
 * service picker with pre-built patterns for common applications. Advanced users can
 * also supply a fully custom regex pattern.
 */

import { useState, useMemo, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import sanitizeText from '@/utils/sanitizeText';
import { toast } from 'react-toastify';
import {
    FaPlus, FaTrash, FaEdit, FaSave, FaTimes, FaRocket,
    FaCheckCircle, FaTimesCircle, FaClock, FaSearch, FaFilter,
    FaLayerGroup, FaCode, FaInfoCircle, FaChevronDown, FaChevronUp
} from 'react-icons/fa';
import './RouterL7RulesTab.scss';
import * as Sentry from '@sentry/react';

// ─────────────────────────────────────────────────────────────────────────────
// Pre-built service catalogue
// Each entry maps a recognisable internet service to the regex pattern that
// MikroTik uses for layer7-protocol matching against SNI / hostname payloads.
// ─────────────────────────────────────────────────────────────────────────────
const PREDEFINED_SERVICES = [
    {
        key:   'whatsapp',
        label: 'WhatsApp',
        emoji: '💬',
        color: '#25D366',
        description: 'WhatsApp messaging and calls',
        regex: 'whatsapp|wa\\.me|whatsapp\\.com|mmg\\.whatsapp\\.net|media\\.whatsapp\\.net'
    },
    {
        key:   'ms365',
        label: 'Microsoft 365',
        emoji: '🏢',
        color: '#0078D4',
        description: 'Office, Teams, Outlook, SharePoint',
        regex: 'microsoft\\.com|office\\.com|outlook\\.com|sharepoint\\.com|microsoftonline\\.com|teams\\.microsoft\\.com'
    },
    {
        key:   'youtube',
        label: 'YouTube',
        emoji: '▶️',
        color: '#FF0000',
        description: 'YouTube video streaming',
        regex: 'youtube\\.com|googlevideo\\.com|ytimg\\.com|youtu\\.be'
    },
    {
        key:   'netflix',
        label: 'Netflix',
        emoji: '🎬',
        color: '#E50914',
        description: 'Netflix video streaming',
        regex: 'netflix\\.com|nflxvideo\\.net|nflximg\\.net|nflxso\\.net'
    },
    {
        key:   'facebook',
        label: 'Facebook / Instagram',
        emoji: '📘',
        color: '#1877F2',
        description: 'Facebook social network and Instagram',
        regex: 'facebook\\.com|fbcdn\\.net|instagram\\.com|cdninstagram\\.com'
    },
    {
        key:   'tiktok',
        label: 'TikTok',
        emoji: '🎵',
        color: '#010101',
        description: 'TikTok short video platform',
        regex: 'tiktok\\.com|tiktokcdn\\.com|musical\\.ly|byteoversea\\.com'
    },
    {
        key:   'twitter',
        label: 'Twitter / X',
        emoji: '🐦',
        color: '#1DA1F2',
        description: 'Twitter / X social network',
        regex: 'twitter\\.com|t\\.co|twimg\\.com|x\\.com'
    },
    {
        key:   'zoom',
        label: 'Zoom',
        emoji: '📹',
        color: '#2D8CFF',
        description: 'Zoom video conferencing',
        regex: 'zoom\\.us|zoomgov\\.com|zoom\\.com'
    },
    {
        key:   'spotify',
        label: 'Spotify',
        emoji: '🎧',
        color: '#1DB954',
        description: 'Spotify music streaming',
        regex: 'spotify\\.com|scdn\\.co|spotifycdn\\.com'
    },
    {
        key:   'telegram',
        label: 'Telegram',
        emoji: '✈️',
        color: '#0088CC',
        description: 'Telegram messaging',
        regex: 'telegram\\.org|t\\.me|telegra\\.ph|telegram\\.me'
    },
    {
        key:   'slack',
        label: 'Slack',
        emoji: '💼',
        color: '#4A154B',
        description: 'Slack workplace messaging',
        regex: 'slack\\.com|slack-edge\\.com|slackb\\.com'
    },
    {
        key:   'discord',
        label: 'Discord',
        emoji: '🎮',
        color: '#5865F2',
        description: 'Discord voice, video and text chat',
        regex: 'discord\\.com|discordapp\\.com|discord\\.gg'
    },
    {
        key:   'gaming',
        label: 'Gaming Platforms',
        emoji: '🕹️',
        color: '#FF6B35',
        description: 'Steam, Epic Games, EA, Battle.net',
        regex: 'steampowered\\.com|epicgames\\.com|battlenet\\.com|origin\\.com|ea\\.com'
    },
    {
        key:   'teamviewer',
        label: 'TeamViewer',
        emoji: '🖥️',
        color: '#0053A0',
        description: 'TeamViewer remote access and support',
        regex: 'teamviewer\\.com|dyngate\\.com|teamviewer\\.io'
    },
    {
        key:   'anydesk',
        label: 'AnyDesk',
        emoji: '🖱️',
        color: '#EF443B',
        description: 'AnyDesk remote desktop',
        regex: 'anydesk\\.com|anydesk\\.de|net\\.anydesk\\.relay'
    },
    {
        key:   'remotedesktop',
        label: 'Remote Desktop',
        emoji: '💻',
        color: '#7B2D8B',
        description: 'RDP, VNC, LogMeIn, Splashtop, Chrome Remote Desktop',
        regex: 'logmein\\.com|splashtop\\.com|remotedesktop\\.google\\.com|realvnc\\.com|tightvnc\\.com'
    },
    {
        key:   'gambling',
        label: 'Gambling',
        emoji: '🎰',
        color: '#DC2626',
        description: 'Online casinos, betting, poker, and gambling sites',
        regex: 'bet365\\.com|betway\\.com|pokerstars\\.com|888\\.com|williamhill\\.com|paddypower\\.com|unibet\\.com|bwin\\.com|ladbrokes\\.com|coral\\.co\\.uk|draftkings\\.com|fanduel\\.com|betfair\\.com|sportingbet\\.com|casino\\.com|jackpotcity\\.com|slotsmillion\\.com|casumo\\.com|leovegas\\.com|mrgreen\\.com|betfred\\.com|betvictor\\.com|hollywoodbets\\.net|supabets\\.co\\.za|lottoland\\.com'
    },
    {
        key:   'adultcontent',
        label: 'Adult Content',
        emoji: '🔞',
        color: '#991B1B',
        description: 'Explicit and adult websites',
        regex: 'pornhub\\.com|xvideos\\.com|xnxx\\.com|xhamster\\.com|redtube\\.com|youporn\\.com|tube8\\.com|spankbang\\.com|brazzers\\.com|onlyfans\\.com|chaturbate\\.com|livejasmin\\.com|stripchat\\.com|cam4\\.com|bongacams\\.com|xxxvideos\\.com|pornhd\\.com|eporner\\.com|ixxx\\.com|txxx\\.com'
    },
    {
        key:   'snapchat',
        label: 'Snapchat',
        emoji: '👻',
        color: '#FFFC00',
        description: 'Snapchat messaging and stories',
        regex: 'snapchat\\.com|snap\\.com|snapads\\.com|sc-cdn\\.net'
    },
    {
        key:   'reddit',
        label: 'Reddit',
        emoji: '🤖',
        color: '#FF4500',
        description: 'Reddit community forums',
        regex: 'reddit\\.com|redd\\.it|redditstatic\\.com|redditmedia\\.com'
    },
    {
        key:   'pinterest',
        label: 'Pinterest',
        emoji: '📌',
        color: '#E60023',
        description: 'Pinterest image sharing',
        regex: 'pinterest\\.com|pinimg\\.com|pinterest\\.co'
    },
    {
        key:   'linkedin',
        label: 'LinkedIn',
        emoji: '💼',
        color: '#0A66C2',
        description: 'LinkedIn professional network',
        regex: 'linkedin\\.com|licdn\\.com|lnkd\\.in'
    },
    {
        key:   'googleservices',
        label: 'Google Services',
        emoji: '🔍',
        color: '#4285F4',
        description: 'Google Search, Drive, Gmail, Maps, etc.',
        regex: 'google\\.com|googleapis\\.com|googlesyndication\\.com|googleadservices\\.com|gstatic\\.com|gmail\\.com|drive\\.google\\.com'
    },
    {
        key:   'appleservices',
        label: 'Apple Services',
        emoji: '🍎',
        color: '#555555',
        description: 'iCloud, App Store, Apple Music, FaceTime',
        regex: 'apple\\.com|icloud\\.com|itunes\\.apple\\.com|mzstatic\\.com|apple-dns\\.net'
    },
    {
        key:   'amazonweb',
        label: 'Amazon / Shopping',
        emoji: '🛒',
        color: '#FF9900',
        description: 'Amazon, eBay, AliExpress, Temu shopping',
        regex: 'amazon\\.com|amazon\\.co|ebay\\.com|aliexpress\\.com|temu\\.com|shein\\.com'
    },
    {
        key:   'dropbox',
        label: 'Cloud Storage',
        emoji: '☁️',
        color: '#0061FF',
        description: 'Dropbox, Google Drive, OneDrive, Box',
        regex: 'dropbox\\.com|dropboxapi\\.com|box\\.com|boxcloud\\.com|drive\\.google\\.com|onedrive\\.live\\.com'
    },
    {
        key:   'vpnservices',
        label: 'VPN Services',
        emoji: '🔒',
        color: '#10B981',
        description: 'NordVPN, ExpressVPN, Surfshark, Proton VPN, etc.',
        regex: 'nordvpn\\.com|expressvpn\\.com|surfshark\\.com|protonvpn\\.com|privateinternetaccess\\.com|cyberghostvpn\\.com|tunnelbear\\.com|windscribe\\.com|mullvad\\.net|hidemyass\\.com'
    },
    {
        key:   'torrent',
        label: 'Torrents / P2P',
        emoji: '⬇️',
        color: '#B45309',
        description: 'BitTorrent trackers and P2P file sharing sites',
        regex: 'thepiratebay\\.org|1337x\\.to|rarbg\\.to|torrentgalaxy\\.to|yts\\.mx|limetorrents\\.cc|bittorrent\\.com|utorrent\\.com|kickass\\.to|nyaa\\.si'
    },
    {
        key:   'cryptomining',
        label: 'Crypto Mining',
        emoji: '⛏️',
        color: '#F59E0B',
        description: 'Browser-based cryptocurrency mining scripts',
        regex: 'coinhive\\.com|crypto-loot\\.com|coin-hive\\.com|jsecoin\\.com|authedmine\\.com|webmine\\.pro|ppoi\\.org|projectpoi\\.com|monerominer\\.rocks|coinimp\\.com'
    },
    {
        key:   'socialmedia',
        label: 'All Social Media',
        emoji: '📱',
        color: '#6366F1',
        description: 'All major social media platforms combined',
        regex: 'facebook\\.com|fbcdn\\.net|instagram\\.com|twitter\\.com|x\\.com|tiktok\\.com|snapchat\\.com|reddit\\.com|pinterest\\.com|linkedin\\.com|tumblr\\.com|threads\\.net'
    },
    {
        key:   'streaming',
        label: 'All Streaming',
        emoji: '📺',
        color: '#7C3AED',
        description: 'Netflix, YouTube, Disney+, Hulu, HBO, Prime Video, Twitch',
        regex: 'netflix\\.com|youtube\\.com|disneyplus\\.com|hulu\\.com|hbomax\\.com|max\\.com|primevideo\\.com|twitch\\.tv|crunchyroll\\.com|peacocktv\\.com|paramountplus\\.com|dstv\\.com|showmax\\.com'
    },
    {
        key:   'dating',
        label: 'Dating Apps',
        emoji: '❤️',
        color: '#EC4899',
        description: 'Tinder, Bumble, Hinge, Badoo, etc.',
        regex: 'tinder\\.com|bumble\\.com|hinge\\.co|badoo\\.com|match\\.com|okcupid\\.com|pof\\.com|zoosk\\.com'
    },
    {
        key:   'custom',
        label: 'Custom Pattern',
        emoji: '⚙️',
        color: '#64748b',
        description: 'Write your own POSIX regex pattern',
        regex: ''
    }
];

/** Default form state used when creating a new rule. */
const DEFAULT_FORM = {
    name:           '',
    description:    '',
    regexPattern:   '',
    serviceType:    null,
    chain:          'forward',
    firewallAction: 'drop'
};

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
const RouterL7RulesTab = ({ l7Rules, loading, onRefresh }) => {
    const { apiFetch } = useAuth();

    // ── UI state ──────────────────────────────────────────────────────────────
    /** Controls visibility of the create / edit form panel. */
    const [showForm, setShowForm]       = useState(false);
    /** 'create' or 'edit' mode for the form. */
    const [formMode, setFormMode]       = useState('create');
    /** ID of the rule currently being edited (null when in create mode). */
    const [editingId, setEditingId]     = useState(null);
    /** Whether to show the raw regex field regardless of service selection. */
    const [showRegex, setShowRegex]     = useState(false);
    /** Set of rule IDs currently being deployed (used to show a spinner). */
    const [deployingIds, setDeployingIds] = useState(new Set());

    // ── Filter / search state ─────────────────────────────────────────────────
    const [searchTerm, setSearchTerm]             = useState('');
    const [actionFilter, setActionFilter]         = useState('all');
    const [statusFilter, setStatusFilter]         = useState('all');

    // ── Form state ────────────────────────────────────────────────────────────
    const [form, setForm] = useState({ ...DEFAULT_FORM });

    // ── Derived / filtered list ───────────────────────────────────────────────
    const filteredRules = useMemo(() => {
        const term = searchTerm.toLowerCase();
        return (l7Rules || []).filter(rule => {
            const matchesSearch = !term
                || rule.name.toLowerCase().includes(term)
                || (rule.description || '').toLowerCase().includes(term)
                || rule.regexPattern.toLowerCase().includes(term);
            const matchesAction = actionFilter === 'all' || rule.firewallAction === actionFilter;
            const matchesStatus = statusFilter === 'all' || rule.deploymentStatus === statusFilter;
            return matchesSearch && matchesAction && matchesStatus;
        });
    }, [l7Rules, searchTerm, actionFilter, statusFilter]);

    // ── Form helpers ──────────────────────────────────────────────────────────

    /** Called when a service card is clicked in the picker grid. */
    const handleServiceSelect = useCallback((service) => {
        setForm(prev => ({
            ...prev,
            serviceType:  service.key === 'custom' ? null : service.key,
            regexPattern: service.regex,
            // Auto-populate name only when creating a new rule (not when editing)
            name: formMode === 'create' && !prev.name
                ? service.label
                : prev.name
        }));
        // Always show the regex field when Custom is selected so the user can type
        setShowRegex(service.key === 'custom');
    }, [formMode]);

    /** Open the form in create mode with a clean slate. */
    const openCreateForm = () => {
        setForm({ ...DEFAULT_FORM });
        setFormMode('create');
        setEditingId(null);
        setShowRegex(false);
        setShowForm(true);
    };

    /** Open the form in edit mode pre-populated with the selected rule's data. */
    const openEditForm = (rule) => {
        setForm({
            name:           rule.name,
            description:    rule.description || '',
            regexPattern:   rule.regexPattern,
            serviceType:    rule.serviceType || null,
            chain:          rule.chain,
            firewallAction: rule.firewallAction
        });
        setFormMode('edit');
        setEditingId(rule.id);
        setShowRegex(true); // Always show regex in edit mode
        setShowForm(true);
    };

    /** Dismiss the form and reset its state. */
    const closeForm = () => {
        setShowForm(false);
        setEditingId(null);
        setForm({ ...DEFAULT_FORM });
        setShowRegex(false);
    };

    // ── API actions ───────────────────────────────────────────────────────────

    /** Validates and submits the create or update form. */
    const handleSubmitForm = async () => {
        if (!form.name.trim()) {
            toast.warning('Please enter a rule name.');
            return;
        }
        if (!form.regexPattern.trim()) {
            toast.warning('Please select a service or enter a regex pattern.');
            return;
        }

        try {
            const body = {
                name:           form.name.trim(),
                description:    form.description.trim() || null,
                regexPattern:   form.regexPattern.trim(),
                serviceType:    form.serviceType || null,
                chain:          form.chain,
                firewallAction: form.firewallAction
            };

            let response;
            if (formMode === 'create') {
                response = await apiFetch('/api/RouterConfig/l7-rules', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body)
                });
            } else {
                response = await apiFetch(`/api/RouterConfig/l7-rules/${editingId}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ ...body, isEnabled: true })
                });
            }

            const result = await response.json();
            if (response.ok && result.success) {
                toast.success(formMode === 'create'
                    ? 'L7 rule created. Use "Deploy" to push it to routers.'
                    : 'L7 rule updated. Re-deploy to apply changes on routers.');
                closeForm();
                onRefresh();
            } else {
                toast.error(result?.error || 'Failed to save L7 rule.');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error saving L7 rule:', error);
            toast.error('Failed to save L7 rule.');
        }
    };

    /** Deploys an L7 rule to all provisioned routers. */
    const handleDeploy = async (ruleId) => {
        setDeployingIds(prev => new Set([...prev, ruleId]));
        try {
            const response = await apiFetch(`/api/RouterConfig/l7-rules/${ruleId}/deploy`, {
                method: 'POST'
            });
            const result = await response.json();
            if (response.ok && result.success) {
                toast.success(result.data?.message || 'L7 rule deployed to routers.');
                onRefresh();
            } else {
                toast.error(result?.error || 'Failed to deploy L7 rule.');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error deploying L7 rule:', error);
            toast.error('Failed to deploy L7 rule.');
        } finally {
            setDeployingIds(prev => {
                const updated = new Set(prev);
                updated.delete(ruleId);
                return updated;
            });
        }
    };

    /** Soft-deletes an L7 rule (also removes it from all routers). */
    const handleDelete = async (rule) => {
        if (!confirm(`Delete the L7 rule "${rule.name}"?\nThis will remove the rule and its firewall filter from all routers.`)) {
            return;
        }
        try {
            const response = await apiFetch(`/api/RouterConfig/l7-rules/${rule.id}`, {
                method: 'DELETE'
            });
            const result = await response.json();
            if (response.ok && result.success) {
                toast.success('L7 rule deleted from all routers.');
                onRefresh();
            } else {
                toast.error(result?.error || 'Failed to delete L7 rule.');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error deleting L7 rule:', error);
            toast.error('Failed to delete L7 rule.');
        }
    };

    // ── Render helpers ────────────────────────────────────────────────────────

    /** Returns the pre-built service object for the rule, or null for custom rules. */
    const getServiceForRule = (rule) =>
        rule.serviceType
            ? PREDEFINED_SERVICES.find(s => s.key === rule.serviceType) || null
            : null;

    /** Renders a coloured deployment status badge. */
    const renderDeployBadge = (status) => {
        switch (status) {
            case 'Deployed':
                return <span className="l7-deploy-badge l7-deploy-badge--deployed">
                    <FaCheckCircle /> Deployed
                </span>;
            case 'PartialFailed':
                return <span className="l7-deploy-badge l7-deploy-badge--partial">
                    <FaTimesCircle /> Partial Fail
                </span>;
            case 'Pending':
                return <span className="l7-deploy-badge l7-deploy-badge--pending">
                    <FaClock /> Pending
                </span>;
            default: // NotDeployed or other
                return <span className="l7-deploy-badge l7-deploy-badge--not-deployed">
                    <FaInfoCircle /> Not Deployed
                </span>;
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Render
    // ─────────────────────────────────────────────────────────────────────────
    return (
        <div className="l7-tab">
            {/* ── Tab header ───────────────────────────────────────────────── */}
            <div className="content-header">
                <div className="header-info">
                    <h3><FaLayerGroup /> L7 Firewall Rules</h3>
                    <p>
                        Control outbound internet services on all routers using MikroTik Layer 7
                        protocol matching. Rules are applied on the <code>forward</code> chain
                        and target traffic by application-layer patterns (regex).
                    </p>
                </div>
                <div className="header-actions">
                    <button className="btn-primary" onClick={openCreateForm}>
                        <FaPlus /> New Rule
                    </button>
                </div>
            </div>

            {/* ── Explainer banner ─────────────────────────────────────────── */}
            <div className="l7-info-banner">
                <FaInfoCircle className="l7-info-icon" />
                <div>
                    <strong>How L7 rules work:</strong> Each rule creates a{' '}
                    <code>layer7-protocol</code> entry and a matching{' '}
                    <code>firewall filter</code> rule on every provisioned router. Use
                    the service picker below to block or allow common applications without
                    needing to write regular expressions manually.
                </div>
            </div>

            {/* ── Create / Edit form ────────────────────────────────────────── */}
            {showForm && (
                <div className="l7-form-panel">
                    <div className="l7-form-header">
                        <h4>{formMode === 'create' ? 'New L7 Firewall Rule' : 'Edit L7 Rule'}</h4>
                        <button className="btn-icon secondary" onClick={closeForm} title="Close">
                            <FaTimes />
                        </button>
                    </div>

                    {/* Service picker — shown only when creating */}
                    {formMode === 'create' && (
                        <div className="l7-service-picker">
                            <p className="l7-picker-label">
                                <strong>Step 1:</strong> Select the service you want to control:
                            </p>
                            <div className="l7-service-grid">
                                {PREDEFINED_SERVICES.map(service => (
                                    <button
                                        key={service.key}
                                        className={`l7-service-card ${form.serviceType === service.key || (service.key === 'custom' && !form.serviceType && showRegex) ? 'selected' : ''}`}
                                        style={{ '--service-color': service.color }}
                                        onClick={() => handleServiceSelect(service)}
                                        title={service.description}
                                    >
                                        <span className="l7-service-emoji">{service.emoji}</span>
                                        <span className="l7-service-label">{service.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Rule details form */}
                    <div className="l7-form-fields">
                        <p className="l7-picker-label">
                            <strong>{formMode === 'create' ? 'Step 2:' : ''} Rule details:</strong>
                        </p>
                        <div className="l7-form-grid">
                            {/* Name */}
                            <div className="l7-form-group">
                                <label htmlFor="l7-name">
                                    Rule Name <span className="required">*</span>
                                </label>
                                <input
                                    id="l7-name"
                                    type="text"
                                    value={form.name}
                                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                                    placeholder="e.g., Block-WhatsApp"
                                    maxLength={100}
                                />
                                <span className="l7-form-hint">
                                    Used as the MikroTik layer7-protocol name — no spaces.
                                </span>
                            </div>

                            {/* Description */}
                            <div className="l7-form-group">
                                <label htmlFor="l7-desc">Description</label>
                                <input
                                    id="l7-desc"
                                    type="text"
                                    value={form.description}
                                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                                    placeholder="Optional note about this rule"
                                    maxLength={500}
                                />
                            </div>

                            {/* Chain */}
                            <div className="l7-form-group">
                                <label htmlFor="l7-chain">Chain</label>
                                <select
                                    id="l7-chain"
                                    value={form.chain}
                                    onChange={e => setForm(f => ({ ...f, chain: e.target.value }))}
                                >
                                    <option value="forward">forward (LAN → WAN)</option>
                                    <option value="input">input (→ router)</option>
                                    <option value="output">output (router →)</option>
                                </select>
                            </div>

                            {/* Action */}
                            <div className="l7-form-group">
                                <label htmlFor="l7-action">Firewall Action</label>
                                <select
                                    id="l7-action"
                                    value={form.firewallAction}
                                    onChange={e => setForm(f => ({ ...f, firewallAction: e.target.value }))}
                                >
                                    <option value="drop">drop (block traffic silently)</option>
                                    <option value="reject">reject (send reset to client)</option>
                                    <option value="accept">accept (explicitly allow)</option>
                                </select>
                            </div>
                        </div>

                        {/* Regex toggle / display */}
                        <div className="l7-regex-section">
                            <button
                                type="button"
                                className="l7-regex-toggle"
                                onClick={() => setShowRegex(v => !v)}
                            >
                                <FaCode />
                                {showRegex ? 'Hide' : 'View / Edit'} Regex Pattern
                                {showRegex ? <FaChevronUp /> : <FaChevronDown />}
                            </button>
                            {showRegex && (
                                <div className="l7-form-group l7-regex-field">
                                    <label htmlFor="l7-regex">
                                        Regex Pattern <span className="required">*</span>
                                    </label>
                                    <textarea
                                        id="l7-regex"
                                        rows={3}
                                        value={form.regexPattern}
                                        onChange={e => setForm(f => ({ ...f, regexPattern: e.target.value }))}
                                        placeholder="e.g., whatsapp|wa\\.me|whatsapp\\.com"
                                        spellCheck={false}
                                    />
                                    <span className="l7-form-hint">
                                        POSIX-compatible regex. MikroTik matches this against the
                                        first 10 packets of each connection&apos;s payload.
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="l7-form-actions">
                        <button className="btn-primary" onClick={handleSubmitForm}>
                            <FaSave />
                            {formMode === 'create' ? 'Create Rule' : 'Save Changes'}
                        </button>
                        <button className="btn-secondary" onClick={closeForm}>
                            <FaTimes /> Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* ── Filter / search bar ───────────────────────────────────────── */}
            <div className="ual-controls-bar">
                <div className="ual-search-wrap">
                    <FaSearch className="ual-search-icon" />
                    <input
                        type="text"
                        className="ual-search-input"
                        placeholder="Search by name, description or pattern…"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        aria-label="Search L7 rules"
                    />
                    {searchTerm && (
                        <button
                            className="ual-search-clear"
                            onClick={() => setSearchTerm('')}
                            title="Clear search"
                        >
                            <FaTimes />
                        </button>
                    )}
                </div>
                <div className="ual-filter-wrap">
                    <FaFilter className="ual-filter-icon" />
                    <select
                        className="ual-filter-select"
                        value={actionFilter}
                        onChange={e => setActionFilter(e.target.value)}
                        aria-label="Filter by firewall action"
                    >
                        <option value="all">All Actions</option>
                        <option value="drop">Drop</option>
                        <option value="reject">Reject</option>
                        <option value="accept">Accept</option>
                    </select>
                </div>
                <div className="ual-filter-wrap">
                    <FaFilter className="ual-filter-icon" />
                    <select
                        className="ual-filter-select"
                        value={statusFilter}
                        onChange={e => setStatusFilter(e.target.value)}
                        aria-label="Filter by deployment status"
                    >
                        <option value="all">All Statuses</option>
                        <option value="Deployed">Deployed</option>
                        <option value="Pending">Pending</option>
                        <option value="PartialFailed">Partial Fail</option>
                        <option value="NotDeployed">Not Deployed</option>
                    </select>
                </div>
                <span className="ual-result-count">
                    {filteredRules.length} / {(l7Rules || []).length} rules
                </span>
            </div>

            {/* ── Rules table ───────────────────────────────────────────────── */}
            <div className="entries-table">
                {loading ? (
                    <div className="loading">Loading L7 rules…</div>
                ) : (l7Rules || []).length === 0 ? (
                    <div className="no-entries">
                        <FaLayerGroup />
                        <p>No L7 firewall rules yet</p>
                        <button className="btn-primary" onClick={openCreateForm}>
                            <FaPlus /> Create First Rule
                        </button>
                    </div>
                ) : filteredRules.length === 0 ? (
                    <div className="no-entries">
                        <FaSearch />
                        <p>No rules match your search or filter</p>
                        <button
                            className="btn-secondary"
                            onClick={() => {
                                setSearchTerm('');
                                setActionFilter('all');
                                setStatusFilter('all');
                            }}
                        >
                            <FaTimes /> Clear Filters
                        </button>
                    </div>
                ) : (
                    <table>
                        <thead>
                            <tr>
                                <th>Service / Name</th>
                                <th>Description</th>
                                <th>Chain</th>
                                <th>Action</th>
                                <th>Regex Pattern</th>
                                <th>Deployment</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredRules.map(rule => {
                                const service     = getServiceForRule(rule);
                                const isDeploying = deployingIds.has(rule.id);

                                return (
                                    <tr key={rule.id}>
                                        {/* Service / Name */}
                                        <td>
                                            <div className="l7-name-cell">
                                                {service && (
                                                    <span
                                                        className="l7-service-badge"
                                                        style={{ '--service-color': service.color }}
                                                        title={service.description}
                                                    >
                                                        {service.emoji}
                                                    </span>
                                                )}
                                                <span className="l7-rule-name">
                                                    {sanitizeText(rule.name)}
                                                </span>
                                            </div>
                                        </td>

                                        {/* Description */}
                                        <td>
                                            <span className="l7-description">
                                                {sanitizeText(rule.description || '—')}
                                            </span>
                                        </td>

                                        {/* Chain */}
                                        <td>
                                            <code className="l7-chain-badge">{sanitizeText(rule.chain)}</code>
                                        </td>

                                        {/* Firewall action */}
                                        <td>
                                            <span className={`l7-action-badge l7-action-badge--${rule.firewallAction}`}>
                                                {rule.firewallAction}
                                            </span>
                                        </td>

                                        {/* Regex pattern (truncated) */}
                                        <td>
                                            <code
                                                className="l7-regex-preview"
                                                title={rule.regexPattern}
                                            >
                                                {rule.regexPattern.length > 50
                                                    ? `${rule.regexPattern.slice(0, 50)}…`
                                                    : rule.regexPattern}
                                            </code>
                                        </td>

                                        {/* Deployment status */}
                                        <td>
                                            <div className="l7-deploy-cell">
                                                {renderDeployBadge(rule.deploymentStatus)}
                                                {rule.deployedRouterCount > 0 && (
                                                    <span className="l7-router-count">
                                                        {rule.deployedRouterCount}/{rule.totalRouterCount} routers
                                                    </span>
                                                )}
                                            </div>
                                        </td>

                                        {/* Row actions */}
                                        <td>
                                            <div className="action-buttons">
                                                {/* Deploy button */}
                                                <button
                                                    className="btn-icon l7-deploy-btn"
                                                    onClick={() => handleDeploy(rule.id)}
                                                    disabled={isDeploying}
                                                    title="Deploy to all routers"
                                                >
                                                    <FaRocket className={isDeploying ? 'spinning' : ''} />
                                                </button>

                                                {/* Edit button */}
                                                <button
                                                    className="btn-icon primary"
                                                    onClick={() => openEditForm(rule)}
                                                    title="Edit rule"
                                                >
                                                    <FaEdit />
                                                </button>

                                                {/* Delete button */}
                                                <button
                                                    className="btn-icon danger"
                                                    onClick={() => handleDelete(rule)}
                                                    title="Delete rule (removes from all routers)"
                                                >
                                                    <FaTrash />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default RouterL7RulesTab;
